import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Star,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  ExternalLink,
  BookOpen,
  User,
  ArrowLeft,
  MessageSquare,
} from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import { usePublicBookletSpot } from "../../application/hooks/useDiscover";
import { getCategoryById } from "../../config/categories";
import { getCategoryGradient } from "../../utils/style-config";

export function PublicSpotDetailPage() {
  const { t } = useTranslation();
  const { bookletId, spotId } = useParams<{
    bookletId: string;
    spotId: string;
  }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePublicBookletSpot(bookletId, spotId);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 px-4 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
          <AlertCircle className="h-10 w-10 text-destructive" />
        </div>
        <h2 className="mb-2 text-lg font-semibold">{t("shared.notFound")}</h2>
        <p className="mb-6 text-center text-sm text-muted-foreground">
          {t("shared.notFoundDescription")}
        </p>
        <Link to="/discover">
          <Button className="bg-indigo-500 text-white hover:bg-indigo-600">
            {t("discover.backToDiscover")}
          </Button>
        </Link>
      </div>
    );
  }

  const { spot, comment, booklet } = data;
  const category = getCategoryById(spot.category);
  const CategoryIcon = category.icon;
  const hasImages = spot.images.length > 0;
  const gradientConfig = getCategoryGradient(spot.category);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 pb-8 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-4xl items-center px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex flex-1 items-center justify-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500">
              <MapPin className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-indigo-600">{t("app.name")}</span>
          </div>
          <div className="w-8" /> {/* Spacer for balance */}
        </div>
      </header>

      <div className="mx-auto max-w-4xl">
        {/* Cover Area */}
        <div className="relative h-48 overflow-hidden sm:h-64">
          {hasImages ? (
            <>
              <img
                src={spot.images[currentImageIndex].url}
                alt={spot.images[currentImageIndex].caption || spot.name}
                className="h-full w-full object-cover"
              />
              {spot.images.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : spot.images.length - 1))
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentImageIndex((prev) => (prev < spot.images.length - 1 ? prev + 1 : 0))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                    {spot.images.map((_: unknown, index: number) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`h-2 w-2 rounded-full transition-colors ${
                          index === currentImageIndex ? "bg-white" : "bg-white/50"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="relative h-full w-full" style={{ background: gradientConfig.gradient }}>
              {/* Decorative circles */}
              <div
                className="absolute -right-8 -top-8 h-32 w-32 rounded-full"
                style={{ backgroundColor: gradientConfig.decor1 }}
              />
              <div
                className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full"
                style={{ backgroundColor: gradientConfig.decor2 }}
              />
              {/* Category icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <CategoryIcon className="h-16 w-16 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-4 pt-6">
          {/* Booklet Context Card */}
          <Link
            to={`/booklets/public/${booklet.id}`}
            className="mb-6 flex items-center gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-muted/50"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{t("publicSpot.fromBooklet")}</p>
              <p className="truncate font-medium">{booklet.title}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {booklet.author.image ? (
                <img
                  src={booklet.author.image}
                  alt={booklet.author.name}
                  className="h-5 w-5 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted">
                  <User className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
              <span className="truncate">{booklet.author.name}</span>
            </div>
          </Link>

          {/* Title */}
          <h1 className="text-2xl font-bold">{spot.name}</h1>
          {spot.address && (
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0" />
              {spot.address}
            </p>
          )}

          {/* Meta info */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {/* Category */}
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
              style={{
                backgroundColor: `${category.color}15`,
                color: category.color,
              }}
            >
              <CategoryIcon className="h-4 w-4" />
              {t(category.labelKey)}
            </div>

            {/* Rating */}
            {spot.rating && (
              <div className="flex items-center gap-1 text-sm">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-4 w-4 ${
                      star <= spot.rating!
                        ? "fill-amber-400 text-amber-400"
                        : "text-muted-foreground/30"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Visit date */}
            {spot.visitedAt && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {formatDate(spot.visitedAt)}
              </div>
            )}
          </div>

          {/* Description */}
          {spot.description && (
            <p className="mt-4 whitespace-pre-wrap text-muted-foreground">{spot.description}</p>
          )}

          {/* Comment from booklet (author's note) */}
          {comment && (
            <div className="mt-6 rounded-lg border bg-indigo-50/50 p-4 dark:bg-indigo-950/20">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-indigo-700 dark:text-indigo-400">
                <MessageSquare className="h-4 w-4" />
                {t("publicSpot.authorComment")}
              </div>
              <p className="text-muted-foreground">{comment}</p>
            </div>
          )}

          {/* Tags */}
          {spot.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {spot.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="rounded-full bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Map Link */}
          {spot.latitude && spot.longitude && (
            <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200">
                    <MapPin className="h-5 w-5 text-slate-600" />
                  </div>
                  <span className="font-medium">{t("spots.viewOnMap")}</span>
                </div>
                <Button size="sm" asChild>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${spot.latitude},${spot.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("spots.openInMaps")}
                  </a>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
